// Full Residential Listing Data Form schema — mirrors the user's PDF.
// Field types:
//   text | number | tel | date | textarea | select | radio | toggle | multi | grid
// "multi" = checkbox group with options (array of strings)
// "radio" = radio group (options = array of strings)
// "toggle" = single boolean checkbox (yes/on)
// "grid" = matrix: rows × cols of number inputs (stored as { [rowId]: { [colId]: number } })

import { INCLUSION_OPTIONS } from "./data";

const YN = ["Yes", "No"];

const HOA_AMENITIES = [
  "Alarm System Paid", "Barbecue", "Biking Trails", "Bocce Ball Court", "Cable TV Paid",
  "Club House", "Common RV Parking", "Concierge", "Controlled Access", "Earthquake Insurance",
  "Electricity Paid", "Fire Pit", "Gas Paid", "Gated", "Golf Course", "Gym Room",
  "Hiking Trails", "Horse Trails", "Insurance Paid", "Interest Paid", "Maintenance Paid",
  "On Site Security", "On site Property Mgmt", "Pet Rules", "Pets Not Permitted",
  "Pets Permitted", "Pickleball Court", "Picnic Area", "Playground", "Pool",
  "Racquet Ball Court", "Sauna", "Security", "Sewer Paid", "Snow Removal", "Spa",
  "Storage Area", "Tennis Court", "Trash Paid", "Water Paid", "Other (See Remarks)",
];

const PROPERTY_TYPES = ["Single Family", "Condominium", "PUD", "Mobile (w/o land)", "Recreational", "Townhouse", "Twin"];

const STYLES = [
  "2-Story", "A-Frame", "Basement", "Bungalow/Cottage", "Cabin",
  "Condo, High Rise", "Condo, Main Level", "Condo, Middle Level", "Condo, Studio", "Condo, Top Level",
  "Manufactured", "Mid-Century Modern", "Mobile", "Modular", "Patio Home",
  "Rambler/Ranch", "Southwest", "Split-Entry/Bi-Level", "Townhouse, Row-End", "Townhouse, Row-Mid",
  "Tri/Multi-Level", "Tudor", "Victorian", "See Remarks",
];

const CONSTRUCTION_STATUS = ["Built/Standing", "Under Construction", "To Be Built"];

const FRONTAGE_DIR = ["N", "S", "E", "W", "NE", "SE", "NW", "SW"];

const BASEMENT_TYPE = [
  "Daylight", "Entrance", "Full", "None/Crawl Space", "Partial",
  "Shelf", "Slab", "Walkout", "See Remarks",
];

const ACCESSIBILITY = [
  '32" Wide Doorways', '36" Wide Hallways', "Access to Elec. Outlet", "Audible Alerts",
  "Ceiling Lift System", "Elevator", "Front-Cntrld Stove / Oven", "Fully Accessible",
  "Grab Bars", "Ground Level", "Modified Kitchen", "No-Step Entry", "Porch Lift",
  "Ramp", "Roll-In Shower", "Single Level Living", "Stair Lift", "Universal Design",
  "Visible Alerts", "Visitable", "Wheelchair Access", "See Remarks",
];

const DRIVEWAY = ["Asphalt", "Circular", "Common Drive", "Concrete", "Dirt", "Gravel", "Heated", "See Remarks"];

const ENV_CERTS = ["Built Green", "Energy Star", "Home Energy Rating", "Leed", "See Remarks"];

const EXTERIOR = [
  "Aluminum", "Asbestos Shingles", "Asphalt Shingles", "Brick", "Cedar", "Cement Board",
  "Cinder Block", "Clapboard/Masonite", "Composition", "Concrete", "Container", "Frame",
  "Glass", "Insulated Concrete Forms", "Log", "Metal", "Other Wood", "Redwood",
  "Stone", "Straw Bale", "Stucco", "Vinyl", "See Remarks",
];

const AC = [
  "Active Solar", "Central Air; Electric", "Central Air; Gas", "Evap. Cooler: Roof",
  "Evap. Cooler: Window", "Geothermal", "Heat Pump", "Natural Ventilation",
  "Passive Solar", "Refrig. Air; Window", "Seer 16 Or Higher", "See Remarks",
];

const AMENITIES = [
  "Cable TV Available", "Cable TV Wired", "Clubhouse", "Electric Dryer Hookup",
  "Exercise Room", "Gas Dryer Hookup", "Gated Community", "Home Warranty",
  "Park / Playground", "Pickleball Court", "Sauna/Steam Room", "Ski-In/Ski-Out",
  "Swimming Pool", "Tennis Court", "Workshop", "See Remarks",
];

const EXT_FEATURES = [
  "Atrium", "Attic Fan", "Awnings", "Balcony", "Barn", "Basement Entrance",
  "Bay Box Windows", "Deck: Covered", "Double Pane Windows", "Entry (Foyer)",
  "Greenhouse Windows", "Horse Property", "Out Buildings", "Outdoor Lighting",
  "Patio: Covered", "Patio: Open", "Porch: Open", "Porch: Screened",
  "Secured Building", "Secured Parking", "Skylights", "Sliding Glass Doors",
  "Stained Glass Windows", "Storm Doors", "Storm Windows", "Triple Pane Windows",
  "Walk Out", "See Remarks",
];

const GARAGE = [
  "2 Car Deep (Tandem)", "Attached", "Built-in", "Detached",
  "Electrical Vehicle Charging Station", "Extra Height", "Extra Length", "Extra Width",
  "Heated", "Opener", "Parking: Covered", "Parking: Uncovered",
  "RV Electrical Hookup", "RV Parking", "Storage Above", "Workbench", "Workshop", "See Remarks",
];

const HEATING = [
  ">= 95% efficiency", "Active Solar", "Electric", "Electric: Baseboard",
  "Electric: Radiant", "Forced Air", "Gas: Central", "Gas: Radiant", "Gas: Stove",
  "Geothermal", "Gravity Heater", "Heat Pump", "Heat Recovery", "Hot Water",
  "Hydronic", "Oil", "Passive Solar", "Propane", "Radiant: In Floor",
  "Space Heater", "Steam", "Wall Heater", "Wood Burning", "See Remarks",
];

const FLOOR = [
  "Bamboo", "Carpet", "Concrete", "Cork", "Hardwood", "Laminate", "Linoleum",
  "Marble", "Natural Rock", "Slate", "Tile", "Travertine", "Vinyl", "Vinyl (LVP)", "See Remarks",
];

const INT_FEATURES = [
  "Accessory Apartment", "Alarm: Fire", "Alarm: Security", "Bar: Dry", "Bar: Wet",
  "Basement Apartment", "Bath: Primary", "Bath: Sep. Tub / Shower", "Central Vacuum",
  "Closet: Walk-in", "Den/Office", "Dishwasher, Built-in", "Disposal", "Floor Drains",
  "French Doors", "Gas Log", "Granite Countertops", "Great Room", "Instantaneous Hot Water",
  "Intercom", "Jetted Tub", "Kitchen: Second", "Kitchen: Updated", "Laundry Chute",
  "Low VOC Finishes", "Marble Countertops", "Mother-in-law Apt.", "Oven: Double",
  "Oven: Gas", "Oven: Wall", "Quartz Countertops", "Range / Oven, Built-in",
  "Range / Oven, Free Stdng.", "Range, Countertop", "Range, Down Vent", "Range, Gas",
  "Silestone Countertops", "Theater Room", "Vaulted Ceilings", "See Remarks",
];

const LANDSCAPING = [
  "Fruit Trees", "Landscaping: Full", "Landscaping: Part", "Mature Trees", "Pines",
  "Scrub Oak", "Stream", "Terraced Yard", "Vegetable Garden", "Waterfall",
  "Xeriscaped", "See Remarks",
];

const POOL_FEATURES = [
  "Above Ground", "Concrete/Gunite", "Electronic Cover", "Fenced", "Fiberglass/Vinyl",
  "Heated", "In Ground", "Indoor", "With Spa", "See Remarks",
];

const STORAGE = ["Basement", "Carport", "Garage", "Other", "Patio", "Shed", "See Remarks"];

const UTILITIES = [
  "Gas: Available", "Gas: Connected", "Gas: Not Available", "Gas: Not Connected",
  "Power: Available", "Power: Connected", "Power: Not Available", "Power: Not Connected",
  "Sewer: Available", "Sewer: Connected", "Sewer: Not Available", "Sewer: Not Connected",
  "Sewer: Private", "Sewer: Public", "Sewer: Septic Tank",
  "Water: Available", "Water: Connected", "Water: Not Available", "Water: Not Connected",
  "See Remarks",
];

const LOT_FACTS = [
  "Additional Land Available", "Adjacent to Golf Course", "Corner Lot", "Cul-de-sac",
  "Curb & Gutter", "Drip Irrigation: Auto-full", "Drip Irrigation: Manual-full",
  "Drip Irrigation: Manual-part", "Fenced, Full", "Fenced, Part", "Greywater Collection",
  "Pervious Paving", "Private", "Rainwater Collection", "Road: Paved", "Road: Unpaved",
  "Secluded Yard", "Sidewalks", "Sprinkler: Auto-full", "Sprinkler: Auto-part",
  "Sprinkler: Manual-full", "Sprinkler: Manual-part",
  "Terrain: Flat", "Terrain: Grad Slope", "Terrain: Hilly", "Terrain: Mountain", "Terrain: Steep Slope",
  "View: Lake", "View: Mountain", "View: Red Rock", "View: Valley", "View: Water",
  "Waterfront", "Wooded", "See Remarks",
];

const ROOF = [
  "Aluminum", "Asbestos Shingles", "Asphalt Shingles", "Bitumen", "Composition",
  "Fiberglass", "Flat", "Membrane", "Pitched", "PVC", "Rolled-silver",
  "Rubber (EPDM)", "Stone", "Tar/Gravel", "Tile", "Wood Shake Shingles", "See Remarks",
];

const SHOW_INSTRUCTIONS = [
  "Agent Has Key", "Appt/Use Lockbox", "Call Agent/Appt", "Call Owner/Appt",
  "Call Showing Service", "Call Tenant/Appt", "Call Use / Lockbox", "Combination Box",
  "Key At List Office", "Key Box: Electronic", "Knock / Use Lockbox", "Use Aligned Showings",
  "Use Showing Service", "Vacant", "See Remarks",
];

const TELECOM = [
  "Broadband Cable", "DSL", "Ethernet, Wired", "Fiber", "Multiple Phone Lines",
  "T-1 Line", "See Remarks",
];

const TERMS = [
  "Assumption: Qualify", "Assumption: Simple", "Cash", "Commercial Fin. Req.",
  "Conventional", "Conventional Rehabilitation Loan", "Cryptocurrency", "Down Payment Assist.",
  "Exchange", "FHA", "FHA 203(k) - Rehabilitation", "Lease Option Monthly",
  "Lease Option Yearly", "Owner 2nd", "Seller Finance", "Seller Will Subordinate",
  "USDA Rural Development", "VA", "See Remarks",
];

const WATER = [
  "Culinary", "Irrigation", "Irrigation: Pressure", "Private", "Rights: Owned",
  "Rights: Rented", "Secondary", "Shares", "Spring", "Well", "See Remarks",
];

const WINDOW_COVERINGS = [
  "Blinds", "Draperies", "Full", "None", "Part", "Plantation Shutters", "Shades", "See Remarks",
];

const ZONING_TYPE = [
  "Agricultural", "Commercial", "Industrial", "Multi-Family",
  "Short Term Rental Allowed", "Single-Family", "See Remarks",
];

const ANIMALS = [
  "Livestock", "None", "Pets 20 - 75 Lbs.", "Pets < 20 Lbs.", "Pets > 75 Lbs.", "See Remarks",
];

const SPECIAL_OWNER_TYPE = [
  "REO/Bank Owned", "HUD Owned", "Agent Owned", "Fractional Ownership", "LLC", "Trust",
];

const SPECIAL_LISTING_CONDITIONS = [
  "Short Sale", "In Foreclosure", "In Probate", "Bankruptcy", "Notice of Default",
  "Trustee Sale", "Price Was Approved by Third Party", "Third Party Approval Required",
  "Offer Under Third Party Review", "Auction",
];

const ROOM_LEVELS = [
  { id: "lvl4", label: "Level 4 (4th Floor)" },
  { id: "lvl3", label: "Level 3 (3rd Floor)" },
  { id: "lvl2", label: "Level 2 (2nd Floor)" },
  { id: "lvl1", label: "Level 1 (1st Floor)" },
  { id: "bsmt1", label: "Basement 1" },
  { id: "bsmt2", label: "Basement 2" },
  { id: "bsmt3", label: "Basement 3" },
];
// Column groups — top header row spans the group, bottom row shows the individual columns.
// Click any group label to see what each abbreviation means (URE-style popover).
const ROOM_COL_GROUPS = [
  {
    id: "sqft",
    label: "Approx SQ FT",
    cols: [{ id: "sqft", width: 90 }],
  },
  {
    id: "bedP",
    label: "Bed (Primary)",
    primary: true,
    hint: "Mark the level where the primary bedroom / primary suite is located. Enter 1 in that level's row.",
    cols: [{ id: "bedP" }],
  },
  {
    id: "bed",
    label: "Bedrooms",
    hint: "Number of non-primary bedrooms on this level.",
    cols: [{ id: "bed" }],
  },
  {
    id: "bath",
    label: "Bath",
    detail: [
      ["F", "Full bathroom — toilet, sink, tub AND shower"],
      ["¾", "Three-quarter bathroom — toilet, sink, shower (no tub)"],
      ["H", "Half bathroom (powder) — toilet and sink only"],
    ],
    cols: [
      { id: "bathF", label: "F" },
      { id: "bath34", label: "¾" },
      { id: "bathH", label: "H" },
    ],
  },
  { id: "family", label: "Family", hint: "Family room / great room (informal living space).", cols: [{ id: "family" }] },
  { id: "den", label: "Den", hint: "Den, office, study, or library.", cols: [{ id: "den" }] },
  { id: "formalLR", label: "Formal LR", hint: "Formal living room (separate from the family room).", cols: [{ id: "formalLR" }] },
  {
    id: "kitchen",
    label: "Kitchen / Dining",
    detail: [
      ["K", "Kitchen — the main cooking space"],
      ["B", "Breakfast bar or breakfast nook"],
      ["F", "Formal Dining — a separate room used for dining"],
      ["S", "Semi-formal Dining — part of the kitchen used for dining"],
    ],
    cols: [
      { id: "kdK", label: "K" },
      { id: "kdB", label: "B" },
      { id: "kdF", label: "F" },
      { id: "kdS", label: "S" },
    ],
  },
  { id: "laundry", label: "Laundry", hint: "Laundry room or hookups on this level.", cols: [{ id: "laundry" }] },
  { id: "fireplace", label: "Fireplace", hint: "Number of fireplaces on this level.", cols: [{ id: "fireplace" }] },
];

// Flat list of every leaf column id — kept for the schema's `cols` field so the form storage doesn't change.
const ROOM_COLS = ROOM_COL_GROUPS.flatMap((g) => g.cols.map((c) => ({ id: c.id, label: c.label })));

export const LISTING_SCHEMA = {
  steps: [
    {
      id: "pricing-address",
      title: "Pricing & Address",
      sections: [
        {
          id: "pricing",
          title: "Pricing Information",
          fields: [
            { name: "list_price", label: "List Price", type: "number", required: true, prefix: "$" },
          ],
        },
        {
          id: "address",
          title: "Address Information",
          fields: [
            { name: "non_standard_address", label: "Non-Standard Address (see Directions)", type: "toggle" },
            { name: "house_number", label: "House Number", type: "text", required: true },
            { name: "house_dir", label: "Direction", type: "select", options: ["", "N", "S", "E", "W"] },
            { name: "street_name", label: "Street Name or Number", type: "text", required: true },
            { name: "street_type", label: "Street Type", type: "text", hint: "St, Ave, Blvd…" },
            { name: "quadrant", label: "Quadrant", type: "select", options: ["", "NW", "NE", "SW", "SE"] },
            { name: "unit_number", label: "Unit Number", type: "text" },
            { name: "north_south", label: "North / South", type: "text" },
            { name: "east_west", label: "East / West", type: "text" },
            { name: "city", label: "City", type: "text", required: true },
            { name: "county", label: "County", type: "text" },
            { name: "state", label: "State", type: "text", required: true },
            { name: "zip", label: "Zip", type: "text", required: true },
            { name: "project_subdivision", label: "Project / Subdivision", type: "text" },
            { name: "zoning_code", label: "Zoning", type: "text" },
          ],
        },
      ],
    },
    {
      id: "schools-community",
      title: "Schools & Community",
      sections: [
        {
          id: "schools",
          title: "School Information",
          fields: [
            { name: "school_district", label: "School District", type: "text" },
            { name: "junior_high", label: "Junior High School", type: "text" },
            { name: "elementary", label: "Elementary School", type: "text" },
            { name: "high", label: "High School", type: "text" },
            { name: "other_school", label: "Other School", type: "text" },
          ],
        },
        {
          id: "community",
          title: "Community Information",
          fields: [
            { name: "hoa", label: "HOA", type: "radio", options: YN },
            { name: "hoa_change_fee", label: "HOA Change Fee", type: "text" },
            { name: "hoa_change_fee_type", label: "Change Fee Type", type: "radio", options: ["Percentage of Sales Price", "Dollar Amount"] },
            { name: "hoa_fee", label: "HOA Fee", type: "number", prefix: "$" },
            { name: "hoa_fee_frequency", label: "Fee Frequency", type: "radio", options: ["Monthly", "Quarterly", "Annually"] },
            { name: "hoa_contact_phone", label: "HOA Contact Phone", type: "tel" },
            { name: "hoa_phone_type", label: "Phone Type", type: "radio", options: ["Voice", "TDD"] },
            { name: "hoa_contact", label: "HOA Contact", type: "text" },
            { name: "hoa_rental_cap", label: "HOA Rental Cap", type: "radio", options: YN },
            { name: "hoa_amenities", label: "HOA Amenities", type: "multi", options: HOA_AMENITIES },
            { name: "hoa_remarks", label: "HOA Remarks", type: "textarea" },
            { name: "short_term_rental_allowed", label: "Short-Term Rental Allowed", type: "radio", options: YN },
            { name: "project_restrictions", label: "Project Restrictions", type: "radio", options: YN },
            { name: "senior_community", label: "Senior Community", type: "radio", options: YN },
            { name: "maintenance_free", label: "Maintenance Free", type: "radio", options: YN },
          ],
        },
      ],
    },
    {
      id: "property-basics",
      title: "Property Basics",
      sections: [
        {
          id: "tax_parcel",
          title: "Tax / Parcel Information",
          fields: [
            { name: "no_assigned_parcel", label: "No Assigned Parcel Number", type: "toggle" },
            { name: "tax_parcel_number", label: "Tax Parcel Number", type: "text" },
            { name: "estimated_taxes", label: "Estimated Taxes", type: "number", prefix: "$" },
            { name: "property_type", label: "Property Type", type: "multi", options: PROPERTY_TYPES },
            { name: "style", label: "Style", type: "multi", options: STYLES },
            { name: "year_built", label: "Year Built", type: "number" },
            { name: "effective_year_built", label: "Effective Year Built", type: "number" },
            { name: "water_shares", label: "Water Shares", type: "text" },
            { name: "construction_status", label: "Construction Status", type: "radio", options: CONSTRUCTION_STATUS },
          ],
        },
      ],
    },
    {
      id: "lot-parking",
      title: "Lot & Parking",
      sections: [
        {
          id: "lot_dim",
          title: "Lot Dimensions",
          fields: [
            { name: "acres", label: "Acres", type: "number" },
            { name: "frontage", label: "Frontage", type: "text" },
            { name: "side", label: "Side", type: "text" },
            { name: "back", label: "Back", type: "text" },
            { name: "irregular_shape", label: "Irregular Shape", type: "radio", options: YN },
            { name: "frontage_facing", label: "Frontage Facing", type: "multi", options: FRONTAGE_DIR },
          ],
        },
        {
          id: "parking",
          title: "Parking",
          fields: [
            { name: "garage_capacity", label: "Garage Capacity", type: "number" },
            { name: "carport_capacity", label: "Carport Capacity", type: "number" },
            { name: "parking_capacity", label: "Parking Capacity", type: "number" },
            { name: "rv_height", label: "R.V. Parking Height", type: "text" },
            { name: "rv_length", label: "R.V. Parking Length", type: "text" },
          ],
        },
        { id: "driveway", title: "Driveway", fields: [{ name: "driveway", label: "Driveway", type: "multi", options: DRIVEWAY }] },
        { id: "lot_facts", title: "Lot Facts", fields: [{ name: "lot_facts", label: "Lot Facts", type: "multi", options: LOT_FACTS }] },
      ],
    },
    {
      id: "rooms-basement",
      title: "Rooms & Basement",
      sections: [
        {
          id: "property_info",
          title: "Property Information",
          fields: [
            { name: "rooms", label: "Rooms by level", type: "grid", rows: ROOM_LEVELS, cols: ROOM_COLS, colGroups: ROOM_COL_GROUPS, flatCols: ROOM_COLS },
            { name: "num_decks", label: "# of Decks", type: "number" },
            { name: "num_patios", label: "# of Patios", type: "number" },
          ],
        },
        {
          id: "basement",
          title: "Basement Information",
          fields: [
            { name: "basement_type", label: "Basement Type", type: "multi", options: BASEMENT_TYPE },
            { name: "basement_finished", label: "Basement Finished", type: "toggle" },
          ],
        },
      ],
    },
    {
      id: "systems",
      title: "Systems",
      sections: [
        { id: "heating", title: "Heating", fields: [{ name: "heating", label: "Heating", type: "multi", options: HEATING }] },
        { id: "ac", title: "Air Conditioning", fields: [{ name: "ac", label: "Air Conditioning", type: "multi", options: AC }] },
        { id: "utilities", title: "Utilities", fields: [{ name: "utilities", label: "Utilities", type: "multi", options: UTILITIES }] },
        { id: "water", title: "Water", fields: [{ name: "water", label: "Water", type: "multi", options: WATER }] },
        { id: "telecom", title: "Telecommunications", fields: [{ name: "telecom", label: "Telecommunications", type: "multi", options: TELECOM }] },
        { id: "roof", title: "Roof", fields: [{ name: "roof", label: "Roof", type: "multi", options: ROOF }] },
      ],
    },
    {
      id: "features-materials",
      title: "Features & Materials",
      sections: [
        { id: "exterior", title: "Exterior", fields: [{ name: "exterior", label: "Exterior", type: "multi", options: EXTERIOR }] },
        { id: "ext_features", title: "Exterior Special Features", fields: [{ name: "ext_features", label: "Exterior Special Features", type: "multi", options: EXT_FEATURES }] },
        { id: "int_features", title: "Interior Special Features", fields: [{ name: "int_features", label: "Interior Special Features", type: "multi", options: INT_FEATURES }] },
        { id: "floor", title: "Floor", fields: [{ name: "floor", label: "Floor", type: "multi", options: FLOOR }] },
        { id: "window_coverings", title: "Window Coverings", fields: [{ name: "window_coverings", label: "Window Coverings", type: "multi", options: WINDOW_COVERINGS }] },
        { id: "amenities", title: "Amenities", fields: [{ name: "amenities", label: "Amenities", type: "multi", options: AMENITIES }] },
        { id: "accessibility", title: "Accessibility Features", fields: [{ name: "accessibility", label: "Accessibility Features", type: "multi", options: ACCESSIBILITY }] },
        { id: "landscaping", title: "Landscaping", fields: [{ name: "landscaping", label: "Landscaping", type: "multi", options: LANDSCAPING }] },
        { id: "storage", title: "Storage", fields: [{ name: "storage", label: "Storage", type: "multi", options: STORAGE }] },
        { id: "garage", title: "Garage / Parking Features", fields: [{ name: "garage", label: "Garage / Parking", type: "multi", options: GARAGE }] },
        { id: "env_certs", title: "Environmental Certifications", fields: [{ name: "env_certs", label: "Environmental Certs", type: "multi", options: ENV_CERTS }] },
      ],
    },
    {
      id: "inclusions-exclusions",
      title: "Inclusions & Exclusions",
      sections: [
        {
          id: "inclusions_exclusions_intro",
          title: "What stays, what goes",
          fields: [
            {
              name: "inclusions",
              label: "Items INCLUDED with the sale",
              type: "multi",
              options: INCLUSION_OPTIONS,
              hint: "Check anything that conveys with the home. If it's bolted down or built-in, it's usually assumed to stay — but listing it removes any doubt.",
            },
            {
              name: "exclusions",
              label: "Items EXCLUDED from the sale",
              type: "multi",
              options: INCLUSION_OPTIONS,
              hint: "Check anything that you want to keep / will take with you. Heirloom chandelier, washer/dryer, hot tub, mounted TV, etc.",
            },
            {
              name: "inclusions_notes",
              label: "Additional notes",
              type: "textarea",
              hint: "Use this for anything custom — \"refrigerator stays except wine fridge in basement,\" timing on pool table delivery, etc.",
            },
          ],
        },
      ],
    },
    {
      id: "pool-adu-solar-pets",
      title: "Pool, ADU, Solar & Pets",
      sections: [
        {
          id: "pool",
          title: "Pool / Spa",
          fields: [
            { name: "pool_available", label: "Pool Available?", type: "radio", options: YN },
            { name: "pool_features", label: "Pool Features", type: "multi", options: POOL_FEATURES },
            { name: "spa_available", label: "Spa Available?", type: "radio", options: YN },
            { name: "community_pool", label: "Community Pool?", type: "radio", options: YN },
          ],
        },
        {
          id: "adu",
          title: "Accessory Dwelling Unit (ADU)",
          fields: [
            { name: "adu", label: "ADU", type: "radio", options: YN },
            { name: "adu_kitchen", label: "Kitchen", type: "radio", options: ["Attached", "Detached"] },
            { name: "adu_sq_ft", label: "Approx Sq. Ft.", type: "number" },
            { name: "adu_beds", label: "Beds", type: "number" },
            { name: "adu_bath", label: "Bath", type: "number" },
            { name: "adu_separate_entrance", label: "Separate Entrance", type: "radio", options: YN },
            { name: "adu_separate_water", label: "Separate Water Meter", type: "radio", options: YN },
            { name: "adu_separate_gas", label: "Separate Gas Meter", type: "radio", options: YN },
            { name: "adu_separate_electric", label: "Separate Electric Meter", type: "radio", options: YN },
            { name: "adu_currently_rented", label: "Currently Rented", type: "radio", options: YN },
            { name: "adu_monthly_rent", label: "Current Monthly Rent", type: "number", prefix: "$" },
            { name: "adu_remarks", label: "ADU Remarks", type: "textarea" },
          ],
        },
        {
          id: "solar",
          title: "Solar",
          fields: [
            { name: "has_solar", label: "Has Solar?", type: "radio", options: YN },
            { name: "solar_ownership", label: "Ownership", type: "radio", options: ["Solar Leased", "Solar Owned", "Solar Financed"] },
            { name: "solar_install_date", label: "Install Date", type: "date" },
            { name: "solar_leasing_company", label: "Leasing Company", type: "text" },
            { name: "solar_finance_company", label: "Finance Company", type: "text" },
          ],
        },
        {
          id: "pets",
          title: "Pet Information",
          fields: [
            { name: "pets_allowed", label: "Pets Allowed", type: "radio", options: YN },
            { name: "animals", label: "Animals", type: "multi", options: ANIMALS },
          ],
        },
      ],
    },
    {
      id: "listing-agent",
      title: "Listing & Agent",
      sections: [
        {
          id: "listing_info",
          title: "Listing Information",
          fields: [
            { name: "effective_date", label: "Effective Date of Listing Agreement", type: "date" },
            { name: "owner_name", label: "Owner Name", type: "text" },
            { name: "expiration_date", label: "Expiration Date", type: "date" },
            { name: "special_owner_type", label: "Special Owner Type", type: "multi", options: SPECIAL_OWNER_TYPE },
            { name: "contact_type", label: "Contact Type", type: "radio", options: ["Agent", "Owner", "Assistant"] },
            { name: "contact_appt", label: "Contact for Appointments & Access", type: "text" },
            { name: "contact_phone_1", label: "Contact Phone 1", type: "tel" },
            { name: "contact_phone_2", label: "Contact Phone 2", type: "tel" },
            { name: "listing_type", label: "Listing Type", type: "radio", options: ["Exclusive Right to Sell (ERS)", "Exclusive Agency (EAL)"] },
            { name: "special_listing_conditions", label: "Special Listing Conditions", type: "multi", options: SPECIAL_LISTING_CONDITIONS },
            { name: "possession", label: "Possession", type: "text" },
          ],
        },
        {
          id: "agent",
          title: "Listing Agent Information",
          fields: [
            { name: "agent", label: "Agent", type: "text" },
            { name: "office_name", label: "Office Name", type: "text" },
            { name: "co_agent", label: "Co-Agent", type: "text" },
          ],
        },
        { id: "terms", title: "Terms", fields: [{ name: "terms", label: "Terms", type: "multi", options: TERMS }] },
        { id: "show_instructions", title: "Show Instructions", fields: [{ name: "show_instructions", label: "Show Instructions", type: "multi", options: SHOW_INSTRUCTIONS }] },
        { id: "zoning_classification", title: "Zoning Classification", fields: [{ name: "zoning_classification", label: "Zoning", type: "multi", options: ZONING_TYPE }] },
      ],
    },
    {
      id: "remarks",
      title: "Remarks",
      sections: [
        {
          id: "remarks",
          title: "Remarks",
          fields: [
            { name: "public_remarks", label: "Public Remarks", type: "textarea", hint: "What a buyer will read." },
            { name: "agent_remarks", label: "Agent Remarks", type: "textarea", hint: "Internal notes for agents only." },
            { name: "exclusions_remarks", label: "Exclusions Remarks", type: "textarea" },
            { name: "directions", label: "Directions / Non-Standard Address", type: "textarea" },
          ],
        },
      ],
    },
  ],
};

// Flatten: yields [{ section, field }] across every step
export function flattenFields(schema = LISTING_SCHEMA) {
  const out = [];
  schema.steps.forEach((step) => {
    step.sections.forEach((sec) => {
      sec.fields.forEach((f) => out.push({ step, section: sec, field: f }));
    });
  });
  return out;
}

// Build rendered sections [{ title, rows: [{ label, value, type, options? }] }] for PDF.
// EVERY section and field is rendered so the PDF always looks like a complete template,
// regardless of what the seller filled in. Empty fields render as a blank line.
// Multi-select fields keep their raw array + full options list so the PDF can render
// checkboxes like an MLS data form.
export function buildRenderedSections(values, schema = LISTING_SCHEMA) {
  const formatScalar = (field, v) => {
    if (v === undefined || v === null || v === "") return "";
    if (field.type === "toggle") return v ? "Yes" : "";
    if (field.type === "number") {
      if (field.prefix === "$") {
        const n = Number(v);
        return isNaN(n) ? String(v) : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
      }
      return String(v);
    }
    if (field.type === "grid") {
      const rows = [];
      field.rows.forEach((r) => {
        const row = v?.[r.id] || {};
        const filled = field.cols.filter((c) => row[c.id]).map((c) => `${c.label}: ${row[c.id]}`);
        if (filled.length) rows.push(`${r.label} — ${filled.join(", ")}`);
      });
      return rows.join("\n");
    }
    return String(v);
  };

  const out = [];
  schema.steps.forEach((step) => {
    step.sections.forEach((sec) => {
      const rows = [];
      sec.fields.forEach((f) => {
        const val = values[f.name];
        if (f.type === "multi") {
          rows.push({
            label: f.label,
            type: "multi",
            options: f.options || [],
            selected: Array.isArray(val) ? val : [],
          });
        } else if (f.type === "grid") {
          rows.push({
            label: f.label,
            type: "grid",
            rows_def: f.rows,
            cols_def: f.cols,
            col_groups: f.colGroups || null,
            data: val || {},
          });
        } else {
          rows.push({
            label: f.label,
            type: f.type || "text",
            value: formatScalar(f, val),
          });
        }
      });
      out.push({ title: sec.title, rows });
    });
  });
  return out;
}
